import uniqid from 'uniqid'

// Catalogue of element types available in the form builder palette.
// `defaults()` returns the (id-less) base shape for a freshly dropped element;
// `createElement` stamps a unique id on top. The `role` field maps an element to
// the e-mail dispatch semantics of the existing Form section (user-email / user-name).
export const ELEMENT_TYPES = [
    {
        type: 'name',
        label: 'Name',
        icon: 'el-icon-fa-user fas',
        single: true,
        defaults: () => ({ type: 'name', role: 'name', label: 'Name', placeholder: '', required: true, width: 'full' }),
    },
    {
        type: 'email',
        label: 'E-Mail',
        icon: 'el-icon-fa-envelope fas',
        single: true,
        locked: true,
        defaults: () => ({ type: 'email', role: 'email', label: 'E-Mail', placeholder: '', required: true, width: 'full' }),
    },
    {
        type: 'text',
        label: 'Textfeld',
        icon: 'el-icon-fa-input-text fas',
        svg: '<svg viewBox="0 0 48 48" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><g><polygon points="8,9 10,9 10,12 10,16 10,39 8,39 8,42 16,42 16,39 14,39 14,16 14,12 14,9 16,9 16,6 8,6"/><polygon points="4,16 7.021,16 7.021,12 0,12 0,36 7.042,36 7.042,32 4,32"/><polygon points="16.979,12 16.979,16 44,16 44,32 16.958,32 16.958,36 48,36 48,12"/></g></svg>',
        defaults: () => ({ type: 'text', role: 'custom', label: 'Textfeld', placeholder: '', required: false, width: 'full' }),
    },
    {
        type: 'textarea',
        label: 'Freitext',
        icon: 'el-icon-fa-align-left fas',
        defaults: () => ({ type: 'textarea', role: 'custom', label: 'Nachricht', placeholder: '', required: false, rows: 5, width: 'full' }),
    },
    {
        type: 'select',
        label: 'Auswahl',
        icon: 'el-icon-fa-caret-down fas',
        defaults: () => ({ type: 'select', role: 'custom', label: 'Auswahl', placeholder: '', required: false, multiple: false, width: 'full', options: [{ key: 'option-1', value: 'Option 1' }] }),
    },
    {
        type: 'radio',
        label: 'Optionsfelder',
        icon: 'el-icon-fa-circle-dot fas',
        defaults: () => ({ type: 'radio', role: 'custom', label: 'Optionsfelder', required: false, width: 'full', inline: true, options: [{ key: 'option-1', value: 'Option 1' }] }),
    },
    {
        type: 'checkbox',
        label: 'Checkboxen',
        icon: 'el-icon-fa-square-check fas',
        defaults: () => ({ type: 'checkbox', role: 'custom', label: 'Checkboxen', required: false, width: 'full', inline: true, options: [{ key: 'option-1', value: 'Option 1' }] }),
    },
    {
        type: 'date',
        label: 'Datum / Uhrzeit',
        icon: 'el-icon-fa-calendar-days fas',
        defaults: () => ({ type: 'date', role: 'custom', label: 'Datum', placeholder: '', required: false, width: 'full', mode: 'date', min: '', max: '' }),
    },
    {
        type: 'country',
        label: 'Länderauswahl',
        icon: 'el-icon-fa-earth-europe fas',
        defaults: () => ({ type: 'country', role: 'custom', label: 'Land', placeholder: 'Bitte wählen', required: false, width: 'full', defaultCountry: 'DE' }),
    },
    {
        type: 'slider',
        label: 'Schieberegler',
        icon: 'el-icon-fa-sliders fas',
        defaults: () => ({ type: 'slider', role: 'custom', label: 'Schieberegler', required: false, width: 'full', min: 0, max: 100, step: 1, unit: '' }),
    },
    {
        type: 'upload',
        label: 'Datei-Upload',
        icon: 'el-icon-fa-paperclip fas',
        defaults: () => ({ type: 'upload', role: 'custom', label: 'Datei', required: false, width: 'full', maxFiles: 1, accept: [] }),
    },
    {
        type: 'consent',
        label: 'Datenschutz',
        icon: 'el-icon-fa-shield-halved fas',
        single: true,
        defaults: () => ({ type: 'consent', role: 'custom', label: 'Ich akzeptiere die Datenschutzerklärung', required: true, width: 'full' }),
    },
    {
        type: 'columns-2',
        label: 'Zweispalter',
        icon: 'el-icon-fa-table-columns fas',
        layout: true,
        defaults: () => ({ type: 'columns', role: 'layout', children: [[], []] }),
    },
    {
        type: 'columns-3',
        label: 'Dreispalter',
        icon: 'el-icon-fa-table-columns fas',
        layout: true,
        defaults: () => ({ type: 'columns', role: 'layout', children: [[], [], []] }),
    },
    {
        type: 'heading',
        label: 'Überschrift',
        icon: 'el-icon-fa-heading fas',
        content: true,
        defaults: () => ({ type: 'heading', role: 'presentational', label: 'Überschrift', width: 'full' }),
    },
    {
        type: 'paragraph',
        label: 'Textabsatz',
        icon: 'el-icon-fa-paragraph fas',
        content: true,
        defaults: () => ({ type: 'paragraph', role: 'presentational', label: 'Beschreibungstext …', width: 'full' }),
    },
    {
        type: 'submit',
        label: 'Absenden-Button',
        icon: 'el-icon-fa-paper-plane fas',
        single: true,
        locked: true,
        defaults: () => ({ type: 'submit', role: 'presentational', label: 'Absenden', width: 'full', align: 'right' }),
    },
]

// Quick lookup helpers used by the column / element components.
export const ELEMENT_TYPE_MAP = ELEMENT_TYPES.reduce((map, item) => {
    map[item.type] = item
    return map
}, {})

export function elementIcon(type) {
    return (ELEMENT_TYPE_MAP[type] && ELEMENT_TYPE_MAP[type].icon) || 'el-icon-fa-square fas'
}

// Optional inline-SVG markup for a type (rendered via v-html, inherits currentColor).
// Falls back to the font-icon class when a type has no custom SVG.
export function elementSvg(type) {
    return (ELEMENT_TYPE_MAP[type] && ELEMENT_TYPE_MAP[type].svg) || ''
}

export function elementTypeName(type) {
    return (ELEMENT_TYPE_MAP[type] && ELEMENT_TYPE_MAP[type].label) || type
}

// Element types that may only exist once across the whole form (e.g. e-mail, submit).
export const SINGLE_INSTANCE_TYPES = ELEMENT_TYPES.filter(item => item.single).map(item => item.type)

// Mandatory element types that must not be removable (e-mail recipient + submit button).
export const LOCKED_TYPES = ELEMENT_TYPES.filter(item => item.locked).map(item => item.type)

export function isLockedType(type) {
    return LOCKED_TYPES.includes(type)
}

// Factory: build a fresh element instance for a given type with a unique id.
// The id doubles as the content id on save, so use the `new:` token the backend
// content-sync recognises as "create a new row" (mirrors helpers.addContentToSection).
export function createElement(type) {
    const def = ELEMENT_TYPE_MAP[type]
    if (!def) return null
    return { id: uniqid('new:'), ...def.defaults() }
}

// ---------------------------------------------------------------------------
// Relational content <-> element mapping
// ---------------------------------------------------------------------------
// A FormV2 form is stored as flat _cp_contents rows (see cityoffer-api migration
// 2026_07_06_120000_add_form_layout_columns_to__cp_contents_table):
//   - a "columns" container is a row with type 'columns-2' | 'columns-3' and a
//     responsive `columns` grid-template ([{key,value}] Setting list)
//   - a field is a row with type 'content'; its field type + all props live in
//     `settings` [{key,value,options}]; a child references its container via
//     `parent` and carries its column index in settings 'column'
//   - key is user-name / user-email / additional-field (drives the submit + mail
//     pipeline) or heading / paragraph / submit / columns (non-submitted rows)
// The SAME vocabulary is consumed on the storefront (sections/FormV2). Keep both
// sides in sync when adding element props.

const OPTION_TYPES = ['select', 'radio', 'checkbox']
const PRESENTATIONAL_TYPES = ['heading', 'paragraph', 'submit']
const TEXT_BODY_TYPES = ['heading', 'paragraph'] // stored in content.text (longText)

function settingValue(settings, key, fallback = undefined) {
    const found = (settings || []).find(s => s && s.key === key)
    return found && found.value !== undefined && found.value !== null ? found.value : fallback
}
function settingOptions(settings, key) {
    const found = (settings || []).find(s => s && s.key === key)
    return found && Array.isArray(found.options) ? found.options : []
}
function toNumber(value, fallback) {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : fallback
}
function isContainerType(type) {
    return typeof type === 'string' && type.startsWith('columns-')
}
function containerColumnCount(type) {
    const n = parseInt(String(type).split('-')[1], 10)
    return n >= 2 ? n : 2
}
function columnsTemplate(count) {
    // 1 column on mobile, `count` columns from the md breakpoint up (grid-template-columns)
    return [
        { key: 'xs', value: '1fr' },
        { key: 'md', value: Array(count).fill('1fr').join(' ') },
    ]
}
function contentKeyFor(el) {
    if (el.role === 'name' || el.type === 'name') return 'user-name'
    if (el.role === 'email' || el.type === 'email') return 'user-email'
    if (PRESENTATIONAL_TYPES.includes(el.type)) return el.type
    return 'additional-field'
}

// element -> a single field/presentational content row
function fieldContentFromElement(el, position, parentId, columnIndex) {
    const settings = []
    const put = (key, value) => {
        if (value === undefined || value === null || value === '') return
        settings.push({ key, value: String(value) })
    }
    const putBool = (key, value) => settings.push({ key, value: value ? '1' : '0' })

    put('type', el.type)
    if (el.role) put('role', el.role)
    if (!PRESENTATIONAL_TYPES.includes(el.type)) putBool('required', !!el.required)
    put('placeholder', el.placeholder)
    put('width', el.width)
    if (parentId != null && columnIndex != null) put('column', columnIndex)

    if (el.type === 'textarea') put('rows', el.rows)
    if (el.type === 'slider') { put('min', el.min); put('max', el.max); put('step', el.step); put('unit', el.unit) }
    if (el.type === 'date') { put('mode', el.mode); put('min', el.min); put('max', el.max) }
    if (el.type === 'country') put('defaultCountry', el.defaultCountry)
    if (el.type === 'select') putBool('multiple', !!el.multiple)
    if (el.type === 'radio' || el.type === 'checkbox') putBool('inline', !!el.inline)
    if (el.type === 'upload') {
        put('maxFiles', el.maxFiles)
        if (Array.isArray(el.accept) && el.accept.length) {
            settings.push({ key: 'accept', options: el.accept.map(a => ({ key: a, value: a })) })
        }
    }
    if (el.type === 'submit') put('align', el.align)
    if (el.role === 'hidden') put('defaultValue', el.defaultValue)
    if (OPTION_TYPES.includes(el.type) && Array.isArray(el.options)) {
        settings.push({ key: 'options', options: el.options.map(o => ({ key: o.key, value: o.value })) })
    }
    if (el.type === 'consent' && el.link) {
        put('link-id', el.link.id); put('link-path', el.link.path); put('link-title', el.link.title)
    }

    const isText = TEXT_BODY_TYPES.includes(el.type)
    return {
        id: el.id,
        key: contentKeyFor(el),
        type: 'content',
        title: isText ? null : (el.label || ''),
        text: isText ? (el.label || '') : null,
        position,
        settings,
        parent: parentId != null ? { id: parentId } : { id: null },
    }
}

function containerContentFromElement(el, position) {
    const count = Array.isArray(el.children) ? el.children.length : 2
    return {
        id: el.id,
        key: 'columns',
        type: `columns-${count}`,
        title: null,
        position,
        columns: columnsTemplate(count),
        settings: [],
        parent: { id: null },
    }
}

// full elements tree -> flat content rows. position is a global DFS counter so the
// server's orderBy('position') yields on-screen order; children carry parent + column.
export function elementsToContents(elements) {
    const contents = []
    let position = 0
    const list = elements || []
    for (const el of list) {
        if (el.type === 'columns') {
            contents.push(containerContentFromElement(el, position++))
            const columns = el.children || []
            columns.forEach((column, columnIndex) => {
                const items = column || []
                items.forEach(child => {
                    contents.push(fieldContentFromElement(child, position++, el.id, columnIndex))
                })
            })
        } else {
            contents.push(fieldContentFromElement(el, position++, null, null))
        }
    }
    return contents
}

// single content row -> element (reverse of fieldContentFromElement)
function elementFromContent(content) {
    const settings = content.settings || []
    const type = settingValue(settings, 'type') || 'text'
    const isText = TEXT_BODY_TYPES.includes(type)
    const el = {
        id: String(content.id),
        type,
        role: settingValue(settings, 'role') || 'custom',
        width: settingValue(settings, 'width') || 'full',
        label: isText ? (content.text || '') : (content.title || ''),
    }
    if (!PRESENTATIONAL_TYPES.includes(type)) {
        el.required = ['1', 'true'].includes(String(settingValue(settings, 'required')))
    }
    const placeholder = settingValue(settings, 'placeholder')
    if (placeholder !== undefined) el.placeholder = placeholder

    if (type === 'textarea') el.rows = toNumber(settingValue(settings, 'rows'), 5)
    if (type === 'slider') {
        el.min = toNumber(settingValue(settings, 'min'), 0)
        el.max = toNumber(settingValue(settings, 'max'), 100)
        el.step = toNumber(settingValue(settings, 'step'), 1)
        el.unit = settingValue(settings, 'unit') || ''
    }
    if (type === 'date') {
        el.mode = settingValue(settings, 'mode') || 'date'
        el.min = settingValue(settings, 'min') || ''
        el.max = settingValue(settings, 'max') || ''
    }
    if (type === 'country') el.defaultCountry = settingValue(settings, 'defaultCountry') || 'DE'
    if (type === 'select') el.multiple = settingValue(settings, 'multiple') === '1'
    if (type === 'radio' || type === 'checkbox') el.inline = settingValue(settings, 'inline') === '1'
    if (type === 'upload') {
        el.maxFiles = toNumber(settingValue(settings, 'maxFiles'), 1)
        el.accept = settingOptions(settings, 'accept').map(o => o.key)
    }
    if (type === 'submit') el.align = settingValue(settings, 'align') || 'right'
    if (el.role === 'hidden') el.defaultValue = settingValue(settings, 'defaultValue') || ''
    if (OPTION_TYPES.includes(type)) {
        el.options = settingOptions(settings, 'options').map(o => ({ key: o.key, value: o.value }))
        if (!el.options.length) el.options = [{ key: 'option-1', value: 'Option 1' }]
    }
    if (type === 'consent') {
        const path = settingValue(settings, 'link-path')
        const id = settingValue(settings, 'link-id')
        const title = settingValue(settings, 'link-title')
        el.link = (path || id) ? { id: id || null, path: path || '', title: title || '' } : undefined
    }
    return el
}

// child -> parent content id, tolerant of the fragment shape (`parent { id }`)
function parentIdOf(content) {
    if (content.parent && content.parent.id != null) return String(content.parent.id)
    if (content.parent_id != null) return String(content.parent_id)
    return null
}

// flat content rows -> elements tree. Mirrors stores/page populateGrid: filter the
// flat rows by parent and rebuild the hierarchy client-side (no recursion needed).
export function contentsToElements(contents) {
    const rows = (contents || [])
        .filter(c => c && !c.deleted)
        .slice()
        .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0))

    const childrenByParent = {}
    rows.forEach(c => {
        const pid = parentIdOf(c)
        if (pid) (childrenByParent[pid] = childrenByParent[pid] || []).push(c)
    })

    const elements = []
    rows.forEach(c => {
        if (parentIdOf(c)) return // handled under its container
        if (isContainerType(c.type)) {
            const count = containerColumnCount(c.type)
            const children = Array.from({ length: count }, () => [])
            const kids = childrenByParent[String(c.id)] || []
            kids.forEach(kid => {
                const raw = parseInt(settingValue(kid.settings, 'column', '0'), 10) || 0
                const idx = Math.min(Math.max(raw, 0), count - 1)
                children[idx].push(elementFromContent(kid))
            })
            elements.push({ id: String(c.id), type: 'columns', role: 'layout', children })
        } else {
            elements.push(elementFromContent(c))
        }
    })
    return elements
}
